import React, { useState } from 'react';
import { Container, Typography, Button, Paper, Box, LinearProgress, CircularProgress } from '@mui/material';
import OpenAI from 'openai';
import { questions } from './data/quizData';
import './App.css';

const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

function App() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [reflection, setReflection] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleAnswer = async (option) => {
    if (isLoading) return; // Prevent multiple clicks while loading

    const newResponses = [...responses, option.response];
    
    if (currentQuestion < questions.length - 1) {
      setResponses(newResponses);
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setIsLoading(true);
      setResponses(newResponses);
      
      try {
        // Combine the responses into a meaningful context
        const context = newResponses.join('. ') + '.';

        const completion = await openai.chat.completions.create({
          model: "gpt-4o",
          messages: [
            {
              role: "system",
              content: `You are an AI that helps create first-person reflections in the style of Esther Perel's therapeutic approach.
              Your task is to take the parent's responses and create a first-person narrative from THEIR perspective.
              The reflection should feel like the parent is speaking about their own situation.
              Start with phrases like "I'm finding that..." or "I feel..." or "What I'm struggling with is..."
              Be warm, vulnerable, and authentic while maintaining a hopeful tone about the possibility of positive change.
              Write in a natural, conversational style that captures the parent's voice.
              Keep the reflection between 75-120 words.`
            },
            {
              role: "user",
              content: `Based on these responses about my parenting journey: ${context}\n\nWrite a first-person reflection of how I'm feeling and what I'm experiencing.`
            }
          ],
          temperature: 0.7,
          max_tokens: 500,
        });

        setReflection(completion.choices[0].message.content);
        setShowResult(true);
      } catch (error) {
        console.error('Error generating reflection:', error);
        setReflection('We apologize, but we encountered an error generating your reflection. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
          Parent Coaching Intake 🌟
        </Typography>
        
        {!showResult ? (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <LinearProgress variant="determinate" value={progress} sx={{ mb: 4 }} />
            <Typography variant="h5" gutterBottom>
              Question {currentQuestion + 1} of {questions.length}
            </Typography>
            <Typography variant="h6" sx={{ mb: 4 }}>
              {questions[currentQuestion].question}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {questions[currentQuestion].options.map((option, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  size="large"
                  onClick={() => handleAnswer(option)}
                  disabled={isLoading}
                  sx={{
                    textTransform: 'none',
                    p: 2,
                    '&:hover': {
                      backgroundColor: '#f0f7ff',
                    },
                    position: 'relative'
                  }}
                >
                  {option.text}
                  {isLoading && index === 0 && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        right: 16
                      }}
                    />
                  )}
                </Button>
              ))}
            </Box>
          </Paper>
        ) : (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            {isLoading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '200px',
                gap: 2
              }}>
                <CircularProgress />
                <Typography variant="body1" color="textSecondary">
                  Generating your personalized reflection...
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="h4" gutterBottom sx={{ color: '#2c3e50' }}>
                  Does this capture how you're feeling?
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.2rem', 
                    mb: 4,
                    fontStyle: 'italic',
                    lineHeight: 1.8,
                    color: '#34495e',
                    textAlign: 'left',
                    p: 3,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 1,
                    border: '1px solid #e9ecef'
                  }}
                >
                  {reflection}
                </Typography>
                <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="h5" gutterBottom sx={{ color: '#2c3e50' }}>
                    If this resonates with you, we can help.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
                    Our coaches specialize in helping parents navigate exactly these kinds of challenges.
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="large"
                  >
                    See Your Matched Coaches
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => {
                      setCurrentQuestion(0);
                      setResponses([]);
                      setShowResult(false);
                      setReflection('');
                    }}
                  >
                    Retake the questionnaire
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        )}
      </Box>
    </Container>
  );
}

export default App;
